import {
  makeContentServiceDeleteRequest,
  makeContentServiceGetRequest,
  makeContentServicePostRequest,
} from 'infrastructure/contentService'
import {
  AssetUploadResultData,
  AssetUploadResultsResponse,
  AssetUploadStartResponse,
  AssetUploadStatusResponse,
} from './types'

type ErrorWithStatus = Error & { status?: number }

function parseErrorResponse(response: Response): Promise<never> {
  return response.text().then((rawResponse) => {
    const error = new Error(
      rawResponse || `Request failed with status ${response.status}`,
    ) as ErrorWithStatus
    error.status = response.status
    throw error
  })
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    return parseErrorResponse(response)
  }

  const rawResponse = await response.text()
  try {
    return JSON.parse(rawResponse) as T
  } catch {
    throw new Error(rawResponse)
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }

  return null
}

function normalizeAssetUploadResultsPayload(
  payload: unknown,
): AssetUploadResultsResponse {
  const root = asRecord(payload)
  if (!root) {
    return { data: null, errors: ['Invalid results payload'] }
  }

  const directData = asRecord(root.data)
  if (
    directData &&
    ('discovery' in directData ||
      'summary' in directData ||
      'node_upload_results' in directData ||
      'leaf_upload_results' in directData)
  ) {
    return {
      data: directData as AssetUploadResultData,
      errors: root.errors as string[] | null | undefined,
    }
  }

  const getNestedData = (
    resultsLike: unknown,
  ): AssetUploadResultData | null => {
    if (!Array.isArray(resultsLike) || resultsLike.length === 0) {
      return null
    }

    const firstResult = asRecord(resultsLike[0])
    const resultContainer = asRecord(firstResult?.result)
    const nestedData = asRecord(resultContainer?.data)

    if (!nestedData) {
      return null
    }

    return nestedData as AssetUploadResultData
  }

  const dataContainer = asRecord(root.data)
  const nestedInData = getNestedData(dataContainer?.results)
  if (nestedInData) {
    return {
      data: nestedInData,
      errors: root.errors as string[] | null | undefined,
    }
  }

  const nestedAtRoot = getNestedData(root.results)
  if (nestedAtRoot) {
    return {
      data: nestedAtRoot,
      errors: root.errors as string[] | null | undefined,
    }
  }

  return {
    data: null,
    errors: ['Unable to locate asset upload results data in response'],
  }
}

/**
 * Starts the asset upload pipeline for the selected tree and version.
 *
 * @param treeId - Tree ID.
 * @param versionId - Tree version ID.
 * @param includeImages - Whether image URLs should be scanned.
 * @param includeDocuments - Whether document URLs should be scanned.
 * @param includeAudioVideo - Whether audio/video URLs should be scanned.
 * @returns API response body.
 */
export async function startAssetUpload(
  treeId: string,
  versionId: string,
  includeImages: boolean,
  includeDocuments: boolean,
  includeAudioVideo: boolean,
): Promise<AssetUploadStartResponse> {
  const response = await makeContentServicePostRequest(
    `content/v1/asset-upload/${treeId}:${versionId}/start`,
    {
      include_images: includeImages,
      include_documents: includeDocuments,
      include_audio_video: includeAudioVideo,
    },
  )

  return parseJsonResponse<AssetUploadStartResponse>(response)
}

/**
 * Fetches the current pipeline status for the selected tree and version.
 *
 * @param treeId - Tree ID.
 * @param versionId - Tree version ID.
 * @returns Current pipeline status response.
 */
export async function getAssetUploadStatus(
  treeId: string,
  versionId: string,
): Promise<AssetUploadStatusResponse> {
  const response = await makeContentServiceGetRequest(
    `content/v1/asset-upload/${treeId}:${versionId}/status`,
  )

  return parseJsonResponse<AssetUploadStatusResponse>(response)
}

/**
 * Fetches completed asset upload results for the selected tree and version.
 *
 * @param treeId - Tree ID.
 * @param versionId - Tree version ID.
 * @returns Upload result payload.
 */
export async function getAssetUploadResults(
  treeId: string,
  versionId: string,
): Promise<AssetUploadResultsResponse> {
  const response = await makeContentServiceGetRequest(
    `content/v1/asset-upload/${treeId}:${versionId}/results`,
  )

  const payload = await parseJsonResponse<unknown>(response)
  return normalizeAssetUploadResultsPayload(payload)
}

/**
 * Deletes the asset upload pipeline for the selected tree and version.
 *
 * @param treeId - Tree ID.
 * @param versionId - Tree version ID.
 * @returns API response body.
 */
export async function deleteAssetUpload(
  treeId: string,
  versionId: string,
): Promise<Record<string, unknown> | null> {
  const response = await makeContentServiceDeleteRequest(
    `content/v1/asset-upload/${treeId}:${versionId}/delete`,
  )

  return parseJsonResponse<Record<string, unknown> | null>(response)
}
